import { DataSource, SelectionModel } from '@angular/cdk/collections';
import {
  Component,
  EventEmitter,
  HostBinding,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { MatSort } from '@angular/material/sort';
import { fuseAnimations } from '@fuse/animations';
import {
  CadModelHelper,
  CadModelMaterialType,
  CuttingCadModel,
  CuttingCadModelMaterialLink,
  CuttingClientMaterial,
  CuttingSettings,
  LegacyDialogsService,
  LoadingState,
  MaterialType,
  OrderStatusAction,
  downloadFile,
} from 'cloud-shared-lib';
import * as _ from 'lodash';
import { Observable, Subject, Subscription, merge, of } from 'rxjs';
import {
  catchError,
  concatMap,
  filter,
  finalize,
  flatMap,
  map,
  mergeMap,
  switchMap,
  takeUntil,
  tap,
  timeout,
} from 'rxjs/operators';

import { HttpErrorResponse } from '@angular/common/http';
import { ClientMaterialService } from '@cutting/main/shared/services/client-material.service';
import { FuseLegacyConfirmDialogComponent } from '@fuse/components/legacy-confirm-dialog/legacy-confirm-dialog.component';
import { CadMaterialsLinkDialogComponent } from './cadmaterials-link-dialog/cadmaterials-link-dialog.component';
import { CadModelDetailsDialogComponent } from './cadmaterials-list-dialog/cadmodel-details-dialog.component';
import { CadmodelMatLinkGlanceDialogComponent } from './cadmodel-mat-link-glance-dialog/cadmodel-mat-link-glance-dialog.component';
import { ModelReadSettingsDialogComponent } from './model-read-settings-dialog/model-read-settings-dialog.component';
import { OrderFileDetailDialogComponent } from './order-file-detail-dialog/order-file-detail-dialog.component';
import { OrderModelsService } from './order-models.service';
import { SemifinishedProductTypeSettingDialogComponent } from './semifinished-product-type-setting-dialog/semifinished-product-type-setting-dialog.component';

@Component({
  selector: 'csl-order-models',
  templateUrl: './order-models.component.html',
  styleUrls: ['./order-models.component.scss'],
  animations: fuseAnimations,
  encapsulation: ViewEncapsulation.None,
})
export class OrderModelsComponent implements OnInit, OnDestroy, OnChanges {
  @Input() editableOrder = false;
  @Input() orderId = 0;
  @Input() disableDownloadingModelForClient = false;
  @Input() orderStatusAction: OrderStatusAction;
  @Input() showModelWriteSettings = false;
  @Input() cuttingSettings: CuttingSettings;

  @Output() needSaveOrder: EventEmitter<void> = new EventEmitter();
  @Output() materialsLinked: EventEmitter<CuttingCadModelMaterialLink[]> = new EventEmitter();
  @Output() modelDeleted: EventEmitter<CuttingCadModelMaterialLink[]> = new EventEmitter();

  @ViewChild(MatSort, { static: true }) sort: MatSort;

  @HostBinding('class.cutting-order-models')
  cuttingOrderFiles = true;

  oldModel: CuttingCadModel;
  allowedModelExt = ['.b3d', '.obl', '.fr3d', '.zbprj', '.shn'];

  unsubscribe$: Subject<void> = new Subject();
  orderSaved$: Subject<boolean> = new Subject();
  downloader$: Observable<{
    body: Blob;
    filename: string;
  }>;

  selectedOrderFiles = new SelectionModel<number>(true, []);
  selectedAll = false;

  displayedColumns = [
    'select',
    'name',
    'sign',
    'count',
    'materials',
    'state',
    'size',
    'date',
    'edit',
    'download',
    'delete',
    'menu',
  ];
  preparedFiles: any[] = [];
  dataSource: FilesDataSource | null;
  detailDialogRef: MatDialogRef<OrderFileDetailDialogComponent>;
  linkMaterialsDialogRef: MatDialogRef<CadMaterialsLinkDialogComponent>;
  materialsListDialogRef: MatDialogRef<CadModelDetailsDialogComponent>;
  linkMaterialsGlanceDialogRef: MatDialogRef<CadmodelMatLinkGlanceDialogComponent>;
  modified: Set<CuttingCadModel> = new Set();
  deletingCadModels: Set<CuttingCadModel> = new Set();

  cadModelMaterialParsedSubscription: Subscription;

  action$: Observable<any>;

  constructor(
    public orderModelsService: OrderModelsService,
    private clientMaterialService: ClientMaterialService,
    private injector: Injector,
    public dialog: MatDialog,
    private dialogsService: LegacyDialogsService,
    public snackBar: MatSnackBar
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!_.isNil(changes.disableDownloadingModelForClient?.currentValue)) {
      const disableDownloadingModelForClient = changes.disableDownloadingModelForClient?.currentValue;

      if (disableDownloadingModelForClient) {
        _.pull(this.displayedColumns, 'download');
      }
    }
  }

  ngOnInit(): void {
    this.dataSource = new FilesDataSource(this.orderModelsService, this.sort);

    this.orderModelsService.onCadModelsChanged
      .pipe(
        tap(cadModels => {
          cadModels.forEach(cadModel => {
            cadModel.parsedMaterials
              .filter(
                m => m.materialType === CadModelMaterialType.Linear || m.materialType === CadModelMaterialType.Plate
              )
              .forEach(m => {
                const material = {
                  article: m.linkedMaterialArticle,
                  name: m.linkedMaterialName,
                  inMaterialBaseId: m.linkedMaterialId,
                  type: m.materialType === CadModelMaterialType.Linear ? MaterialType.Linear : MaterialType.Plate,
                  preparedForDeletion: false,
                  cuttings: [],
                  id: 0,
                };
                this.clientMaterialService.updateMaterials('model', material as CuttingClientMaterial);
              });
          });
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();

    this.cadModelMaterialParsedSubscription = this.orderModelsService.onCadModelMaterialsParsed.subscribe(cadModel => {
      const notParsedCount = this.orderModelsService.onCadModelsChanged.value.filter(
        m => m.state === LoadingState.FileUploaded
      ).length;

      if (cadModel && notParsedCount === 0) {
        this.replaceLinkedMaterial(cadModel);
        this.openLinkModelMaterials();
      }
    });
  }

  isDraftOrder(): boolean {
    return (
      this.orderStatusAction === OrderStatusAction.Create ||
      this.orderStatusAction === OrderStatusAction.CuttingCompleted
    );
  }

  canLinkMaterial(cadModel: CuttingCadModel): boolean {
    return cadModel.state === LoadingState.MaterialParsed;
  }

  canShowLinkedMaterials(cadModel: CuttingCadModel): boolean {
    return cadModel.state === LoadingState.MaterialReplaced;
  }

  isUploading(item: CuttingCadModel): boolean {
    return item.state === LoadingState.Uploading;
  }

  isMaterialParsing(item: CuttingCadModel): boolean {
    return item.state === LoadingState.FileUploaded;
  }

  isConverting(item: CuttingCadModel): boolean {
    return item.state === LoadingState.NeedConvertation;
  }

  isMaterialLinking(item: CuttingCadModel): boolean {
    return item.state === LoadingState.MaterialLinked;
  }

  isSemifinishedProductTypeSetting(item: CuttingCadModel): boolean {
    return item.state === LoadingState.NeedSetDraftBlocksMode;
  }

  getWithoutTwins(newFiles: File[]): File[] {
    const differenceWithLoaded = _.differenceBy(
      newFiles,
      this.orderModelsService.onCadModelsChanged.value.map(m => ({ name: m.filename })),
      v => v.name.toLowerCase()
    );

    return _.uniqBy(differenceWithLoaded, f => f.name.toLowerCase());
  }

  selectAll(): void {
    this.selectedOrderFiles.clear();
    this.selectedAll = !this.selectedAll;
    if (this.selectedAll && this.orderModelsService.onCadModelsChanged.value.length > 0) {
      this.selectedOrderFiles.select(...this.orderModelsService.onCadModelsChanged.value.map(d => d.id));
    }
  }

  onChangeFileInput(event: any): void {
    const target: HTMLInputElement = event.target as HTMLInputElement;

    const files = this.getWithoutTwins(Array.from(target.files));

    if (target.files.length - files.length > 0) {
      const removedCount = target.files.length - files.length;
      if (removedCount === 1 && target.files.length === 1) {
        this.snackBar.open($localize`Файл уже был загружен`, 'OK');
      } else {
        this.snackBar.open($localize`${removedCount} файл(ов) уже были загружены`, 'OK');
      }

      if (removedCount === target.files.length) {
        return;
      }
    }

    if (target.files.length > 0) {
      let add$ = of(false);

      if (!this.orderId) {
        add$ = this.dialogsService.confirm($localize`Заказ будет сохранен. Продолжить?`).pipe(
          filter(res => res),
          tap(() => this.needSaveOrder.emit()),
          switchMap(() =>
            this.orderSaved$.pipe(
              timeout(15000),
              catchError(() => of(false))
            )
          ),
          filter(res => res)
        );
      }

      if (this.showModelWriteSettings) {
        add$ = add$.pipe(
          concatMap(() => this.dialogsService.component(ModelReadSettingsDialogComponent, undefined, 400)),
          filter(res => res)
        );
      }

      add$
        .pipe(
          concatMap((res: boolean | { checkFurniture: boolean; checkAssembly: boolean }) =>
            this.addFiles(files, _.isObject(res) ? res : undefined)
          ),
          takeUntil(this.unsubscribe$),
          finalize(() => ((event.target as HTMLInputElement).value = ''))
        )
        .subscribe();
    }
  }

  openListModelMaterials(item: CuttingCadModel): void {
    const parentInjector = this.injector;
    this.materialsListDialogRef = this.dialog.open(CadModelDetailsDialogComponent, {
      disableClose: false,
      panelClass: 'cad-model-material-link-dialog',
      width: '1400px',
      data: {
        modelId: item.id,
      },
      injector: Injector.create({
        providers: [{ provide: OrderModelsService, useValue: parentInjector.get(OrderModelsService) }],
        parent: parentInjector,
      }),
    });
  }

  openLinkModelMaterials(cadModel?: CuttingCadModel): void {
    const parentInjector = this.injector;

    this.linkMaterialsDialogRef = this.dialog.open(CadMaterialsLinkDialogComponent, {
      disableClose: false,
      panelClass: 'cad-model-material-link-dialog',
      width: '1000px',
      injector: Injector.create({
        providers: [{ provide: OrderModelsService, useValue: parentInjector.get(OrderModelsService) }],
        parent: parentInjector,
      }),
    });

    const notLinked = _.isNil(cadModel)
      ? this.orderModelsService.onCadModelsChanged.value.filter(v => v.state === LoadingState.MaterialParsed)
      : [cadModel];

    this.linkMaterialsDialogRef.componentInstance.cadModels = notLinked;

    this.linkMaterialsDialogRef
      .afterClosed()
      .pipe(
        filter<CuttingCadModel[]>(res => !_.isNil(res) && !_.isEmpty(res)),
        flatMap(res => of(...res)),
        mergeMap(res =>
          this.orderModelsService.uploadCadModelLinkMaterials(res, !_.isNil(cadModel)).pipe(
            catchError((error: HttpErrorResponse) => {
              if (error.status === 404) {
                switch (error.error.errorCode) {
                  case 1:
                    this.snackBar.open($localize`Модель не найдена`);
                    break;
                  case 2:
                    this.snackBar.open($localize`Оригинальный файл модели не найден`);
                    break;
                }
              } else if (error.status === 400) {
                switch (error.error?.errorCode) {
                  case 1:
                    this.snackBar.open($localize`Ошибка: Нельзя изменить заказ в текущем статусе`);
                    break;
                  case 2:
                    this.snackBar.open($localize`Ошибка: ${error.error?.message}`);
                    break;
                }
              }

              return of(null);
            })
          )
        ),
        tap(res => {
          const toSetIndex = notLinked.findIndex(nl => nl.guid === res.guid);

          if (toSetIndex !== -1) {
            notLinked[toSetIndex].state = LoadingState.MaterialLinked;

            notLinked[toSetIndex].parsedMaterials = res.parsedMaterials;

            this.materialsLinked.emit(res.parsedMaterials);

            // res.parsedMaterials
            //   .filter(
            //     m => m.materialType === CadModelMaterialType.Linear || m.materialType === CadModelMaterialType.Plate
            //   )
            //   .forEach(m => {
            //     this.clientMaterialCuttingsService.addMaterial({
            //       article: m.linkedMaterialArticle,
            //       name: m.linkedMaterialName,
            //       inMaterialBaseId: m.linkedMaterialId,
            //       type: m.materialType === CadModelMaterialType.Linear ? MaterialType.Linear : MaterialType.Plate,
            //       preparedForDeletion: false,
            //       cuttings: [],
            //       id: 0,
            //     } as CuttingClientMaterial);
            //   });
          }
        }),
        finalize(() => (this.linkMaterialsDialogRef = null))
      )
      .subscribe();
  }

  openGlanceLinkModelMaterials(cadModel: CuttingCadModel): void {
    this.linkMaterialsGlanceDialogRef = this.dialog.open(CadmodelMatLinkGlanceDialogComponent, {
      disableClose: false,
      panelClass: 'cad-model-material-link-glance-dialog',
      width: '1000px',
    });

    this.linkMaterialsGlanceDialogRef.componentInstance.cadModelMaterialLink = cadModel.parsedMaterials;

    this.linkMaterialsGlanceDialogRef
      .afterClosed()
      .pipe(finalize(() => (this.linkMaterialsGlanceDialogRef = null)))
      .subscribe();
  }

  deleteOrderFile(item: CuttingCadModel): void {
    let mess = $localize`Удалить файл ${item.filename}`;

    if (this.orderStatusAction !== OrderStatusAction.Create) {
      mess += ' ' + $localize`Результаты раскроя будут удалены`;
    }

    this.dialogsService
      .confirm(mess)
      .pipe(
        filter(res => res),
        tap(() => {
          this.deletingCadModels.add(item);
          this.orderModelsService.deleteCadModel(item.id).subscribe(() => {
            this.orderModelsService.onCadModelsChanged.value.splice(
              this.orderModelsService.onCadModelsChanged.value.indexOf(item),
              1
            );
            this.orderModelsService.onCadModelsChanged.next(this.orderModelsService.onCadModelsChanged.value);
            this.modelDeleted.emit(this.canShowLinkedMaterials(item) ? item.parsedMaterials : []);
            this.snackBar.open($localize`Файл удален`, 'OK', {
              verticalPosition: 'top',
              duration: 2000,
            });
            this.deletingCadModels.delete(item);
          });
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();
  }

  deleteOrderFiles(): void {
    if (this.selectedOrderFiles.selected.length === 0) {
      this.snackBar.open($localize`Выберите файлы моделей для удаления`);
      return;
    }

    let mess = $localize`Удалить выбранные файлы?`;

    if (this.orderStatusAction !== OrderStatusAction.Create) mess += ' ' + $localize`Результаты раскроя будут удалены`;

    this.dialogsService
      .confirm(mess)
      .pipe(
        filter(res => res),
        concatMap(() => {
          return this.orderModelsService.deleteCadModels(this.selectedOrderFiles.selected);
        }),
        tap(() => {
          const existModels = this.orderModelsService.onCadModelsChanged.value.filter(
            model => !this.selectedOrderFiles.selected.includes(model.id)
          );
          this.orderModelsService.onCadModelsChanged.next(existModels);

          const parsedMaterials = existModels.flatMap(model => model.parsedMaterials);
          this.modelDeleted.emit(parsedMaterials);

          this.selectedOrderFiles.clear();
          this.selectedAll = false;
          this.snackBar.open($localize`Файлы удалены`);
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();
  }

  openDetailOrderFile(item: CuttingCadModel): void {
    this.detailDialogRef = this.dialog.open(OrderFileDetailDialogComponent, {
      panelClass: 'order-file-detail-dialog',
      disableClose: false,
      data: { cuttingSettings: this.cuttingSettings },
    });

    this.detailDialogRef.componentInstance.cadModel = item;
    this.detailDialogRef.componentInstance.forbiddenSigns = this.orderModelsService.onCadModelsChanged.value
      .filter((m: any) => m.id !== item.id)
      .map((m: any) => m.sign);

    this.detailDialogRef
      .afterClosed()
      .pipe(
        map(res => res as CuttingCadModel),
        finalize(() => (this.detailDialogRef = null))
      )
      .subscribe(res => {
        if (!_.isEmpty(res)) {
          const index = (this.orderModelsService.onCadModelsChanged.value as CuttingCadModel[]).findIndex(
            m => m.id === item.id
          );

          if (index >= 0) {
            (this.orderModelsService.onCadModelsChanged.value as CuttingCadModel[])[index].sign = res.sign;
            (this.orderModelsService.onCadModelsChanged.value as CuttingCadModel[])[index].count = res.count;

            this.orderModelsService.onCadModelsChanged.next(this.orderModelsService.onCadModelsChanged.value);

            this.modified.add((this.orderModelsService.onCadModelsChanged.value as CuttingCadModel[])[index]);
          }
        }
      });
  }

  openSemifinishedProductDialog(item: CuttingCadModel): void {
    this.action$ = this.dialogsService
      .component(SemifinishedProductTypeSettingDialogComponent, item.draftBlocks, 700)
      .pipe(
        filter(b => !!b?.length),
        tap(b => ((item.draftBlocks = b), (item.state = LoadingState.FileUploaded))),
        concatMap(() => this.orderModelsService.setCadModelDraftBlockMode(item.id, item.draftBlocks))
      );
  }

  getModelState(state: LoadingState): string {
    let result = '';

    switch (state) {
      case LoadingState.FileUploaded:
        result = $localize`Файл загружен`;
        break;

      case LoadingState.MaterialLinked:
        result = $localize`Материалы сопоставлены`;
        break;

      case LoadingState.MaterialParsed:
        result = $localize`Ожидает сопоставления материалов`;
        break;

      case LoadingState.MaterialReplaced:
        result = $localize`Модель успешно обработана`;
        break;

      case LoadingState.NeedConvertation:
        result = $localize`Ожидает завершения конвертирования`;
        break;

      case LoadingState.NeedSetDraftBlocksMode:
        result = $localize`Ожидает установки типов полуфабрикатов`;
        break;
    }

    return result;
  }

  downloadOrderFile(item: CuttingCadModel): void {
    this.downloader$ = this.orderModelsService.downloadCadModel(item.id).pipe(
      tap(res => {
        downloadFile(res.body, res.filename);
      })
    );
  }

  replaceModel(event: any, cadModel: CuttingCadModel): void {
    let confirmDialogRef = this.dialog.open(FuseLegacyConfirmDialogComponent, {
      disableClose: false,
    });

    let message = $localize`Заменить файл ${cadModel.filename}?`;
    if (this.orderStatusAction !== OrderStatusAction.Create) {
      message += ' ' + $localize`Результаты раскроя будут удалены`;
    }

    confirmDialogRef.componentInstance.confirmMessage = message;

    confirmDialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.orderModelsService.deleteCadModel(cadModel.id).subscribe(() => {
          this.orderModelsService.onCadModelsChanged.value.splice(
            this.orderModelsService.onCadModelsChanged.value.indexOf(cadModel),
            1
          );
          this.onChangeFileInput(event);
        });
        if (cadModel.parsedMaterials.some(element => element.linkedMaterialId > 0)) {
          this.oldModel = _.cloneDeep(cadModel);
        }
      }
      confirmDialogRef = null;
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.cadModelMaterialParsedSubscription.unsubscribe();
  }

  private addFiles(
    files: File[],
    readSettings?: { checkFurniture: boolean; checkAssembly: boolean }
  ): Observable<void> {
    return this.orderModelsService.uploadCadModel(files, this.orderId, readSettings);
  }

  private replaceLinkedMaterial(cadModel: CuttingCadModel): void {
    if (!this.oldModel) return;

    this.oldModel.parsedMaterials.forEach(material => {
      if (material.linkedMaterialId > 0) {
        const cadModelMaterialLink = cadModel.parsedMaterials.find(
          element => element.originalMaterialFullName === material.originalMaterialFullName
        );
        if (!_.isNil(cadModelMaterialLink)) CadModelHelper.replaceLinkedMaterial(cadModelMaterialLink, material);
      }
    });
    this.oldModel = null;
  }
}

export class FilesDataSource extends DataSource<any> {
  constructor(private orderDetailService: OrderModelsService, private sort: MatSort) {
    super();
  }

  /* Connect function called by the table to retrieve one stream containing the data to render. */
  connect(): Observable<any[]> {
    const displayDataChanges = [this.orderDetailService.onCadModelsChanged, this.sort.sortChange];
    return merge(...displayDataChanges).pipe(
      map(() => {
        let data: CuttingCadModel[] = this.orderDetailService.onCadModelsChanged.value.slice();

        data = this.sortData(data);

        // data.forEach(model => {
        //   if (this.canShowLinkedMaterials(model)) {
        //     model.parsedMaterials
        //       .filter(
        //         m => m.materialType === CadModelMaterialType.Linear || m.materialType === CadModelMaterialType.Plate
        //       )
        //       .forEach(m => {
        //         this.clientMaterialCuttingsService.addMaterial({
        //           article: m.linkedMaterialArticle,
        //           name: m.linkedMaterialName,
        //           inMaterialBaseId: m.linkedMaterialId,
        //           type: m.materialType === CadModelMaterialType.Linear ? MaterialType.Linear : MaterialType.Plate,
        //           preparedForDeletion: false,
        //           cuttings: [],
        //           id: 0,
        //         } as CuttingClientMaterial);
        //       });
        //   }
        // });

        return data;
      })
    );
  }

  canShowLinkedMaterials(cadModel: CuttingCadModel): boolean {
    return cadModel.state === LoadingState.MaterialReplaced;
  }

  sortData(data: CuttingCadModel[]): CuttingCadModel[] {
    if (!this.sort.active || this.sort.direction === '') {
      return data;
    }

    return data.sort((a, b) => {
      let propertyA: Date | number | string = '';
      let propertyB: Date | number | string = '';

      switch (this.sort.active) {
        case 'name':
          [propertyA, propertyB] = [a.filename, b.filename];
          break;
        case 'type':
          [propertyA, propertyB] = [a.fileSize, b.fileSize];
          break;
        case 'size':
          [propertyA, propertyB] = [a.count, b.count];
          break;
      }

      const valueA = isNaN(+propertyA) ? propertyA : +propertyA;
      const valueB = isNaN(+propertyB) ? propertyB : +propertyB;

      return (valueA < valueB ? -1 : 1) * (this.sort.direction === 'asc' ? 1 : -1);
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  disconnect(): void {}
}
